import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { UseFormReturn } from "react-hook-form";
import {
  AccountsApiListAndSearchAllAccountsRequest,
  PublicAccount,
  TransactionV2,
} from "@rentablo/api/apiv1";
import {
  useContractForms,
  useGetContractCategories,
} from "@features/contractAnalyzer/hooks";
import {
  ContractFormValues,
  ContractSubCategory,
} from "@features/contractAnalyzer/types";
import { CreateSteps } from "@features/contractAnalyzer/create/types";
import { SelectGroupedOptions } from "@rentablo/components";
import { usePublicOrPrivateAccounts } from "@features/sharedPortfolio/hooks/usePublicOrPrivateAccounts";
import { filterOutHiddenAccounts } from "../../../hooks/useSelectGroupsForId";

export const contractAccountParams: AccountsApiListAndSearchAllAccountsRequest =
  {
    type: ["02_cash"],
    bankConnectionType: ["WEALTH_API"],
  };

interface CreateContractContextProps {
  accounts: PublicAccount[];
  selectedAccountId?: number;
  setSelectedAccountId: (id: number) => void;
  isFetchingAccounts: boolean;
  categories: SelectGroupedOptions[];
  createStep: CreateSteps;
  onStepChange: (step: CreateSteps) => void;
  onStepBack: () => void;
  onClose: () => void;
  subCategoryValue?: ContractSubCategory;
  setSubCategoryVal: (value: ContractSubCategory) => void;
  selectedTransactions: TransactionV2[];
  setSelectedTransactions: (transactions: TransactionV2[]) => void;
  confirmedTransactions: TransactionV2[];
  setConfirmedTransactions: Dispatch<SetStateAction<TransactionV2[]>>;
  isLoading: boolean;
  secondContractForm: UseFormReturn<ContractFormValues>;
}
const CreateContractContext = createContext<
  CreateContractContextProps | undefined
>(undefined);

export const useCreateContractContext = (): CreateContractContextProps => {
  const context = useContext(CreateContractContext);
  if (!context) {
    throw new Error(
      "useCashflowAnalyzer must be used within a CashflowAnalyzerProvider"
    );
  }
  return context;
};

export const CreateContractProvider: FC<{
  children: ReactNode;
  onClose: () => void;
}> = ({ children, onClose }) => {
  const { data: categories, isFetching: isLoadingCategories } =
    useGetContractCategories();
  const { accounts: rawAccounts, isFetching: isFetchingAccounts } =
    usePublicOrPrivateAccounts(contractAccountParams);
  const accounts = useMemo(
    () => filterOutHiddenAccounts(rawAccounts),
    [rawAccounts]
  );
  const { secondContractForm } = useContractForms(undefined);
  const prevSteps = useRef<CreateSteps[]>([]);
  const [createStep, setCreateStep] = useState<CreateSteps>("createContract");
  const [selectedAccountIdState, setSelectedAccountId] = useState<number>();
  const selectedAccountId = selectedAccountIdState ?? accounts[0]?.id;
  const [selectedTransactions, setSelectedTransactions] = useState<
    TransactionV2[]
  >([]);
  const [confirmedTransactions, setConfirmedTransactions] = useState<
    TransactionV2[]
  >([]);
  const [subCategoryVal, setSubCategoryVal] =
    useState<ContractSubCategory>("notClassified");
  const subCategoryValue =
    typeof subCategoryVal === "number" ? subCategoryVal : undefined;

  const onStepChange = (step: CreateSteps) => {
    prevSteps.current.push(createStep);
    if (step === "confirmSelection") {
      setConfirmedTransactions(selectedTransactions);
    }
    setCreateStep(step);
  };

  const onStepBack = () => {
    if (prevSteps.current.length > 0) {
      const lastStep = prevSteps.current[prevSteps.current.length - 1];
      prevSteps.current = prevSteps.current.slice(0, -1);
      setCreateStep(lastStep);
    } else {
      onClose();
    }
  };

  return (
    <CreateContractContext.Provider
      value={{
        accounts,
        selectedAccountId,
        setSelectedAccountId,
        isFetchingAccounts,
        categories,
        createStep,
        onStepChange,
        onStepBack,
        onClose,
        subCategoryValue,
        setSubCategoryVal,
        selectedTransactions,
        setSelectedTransactions,
        confirmedTransactions,
        setConfirmedTransactions,
        isLoading: isLoadingCategories || isFetchingAccounts,
        secondContractForm,
      }}
    >
      {children}
    </CreateContractContext.Provider>
  );
};
